import React, { useState, useEffect, useRef, useLayoutEffect, useCallback, useMemo } from 'react';
import { Box, Paper, Stack } from '@mui/material';

import { SimpleSwitch, SimpleButton, TTSButton, ErrorMessage, Loading } from '@/src/ui';
import { DynamicTextPreview, type QuestionRendererOptions, defaultOptions } from '@/src/lib';
import PointLabelPopup from './components/PointLabelPopup';
import LineEditorPopup from './components/LineEditorPopup';
import { submitForGrading } from '../../services';

import type {
    QtiGraphingQuestionDTO,
    GraphPoint,
    GraphLine,
    StudentPoint,
    PendingPoint,
    LabelEditorState,
    StudentLine,
    LineEditorState,
} from './GraphingRendererDTOs';

interface GraphingRendererProps {
    question: QtiGraphingQuestionDTO;
    showLanguageToggle?: boolean;
    handleUpdateUserResponse?: (payload: string, userInitiated: boolean) => void;
    loadedUserResponse?: string;
    hideHelp?: boolean;
    options?: QuestionRendererOptions;

    maxBoxWidth?: number;
    maxBoxHeight?: number;
}
const GRID_CFG = {
    minorPerMajor: 1,
    showMinor: true,
    showLabels: true,
    labelEveryMajorX: 1,
    labelEveryMajorY: 1,
    fontSize: 12,
    maxLabelsPerAxis: 25,
    minMajorPx: 22,
    minMinorPx: 10,
};
const AXIS_LABELS = {
    x: 'x',
    y: 'y',
    fontSize: 14,
    offset: 6,
};

const COORD_EPS = 1e-6; // quantize to avoid tiny float diffs
const qRound = (v: number, eps = COORD_EPS) => Math.round(v / eps) * eps;
const ptKey = (x: number, y: number, eps = COORD_EPS) => `${qRound(x, eps)}|${qRound(y, eps)}`;

const colorPalette = {
    primary: 'rgba(25, 118, 210, 1)',
    green: 'rgba(0,150,0,0.9)',
    orange: 'rgba(255,165,0,0.95)',
    black: 'rgba(0,0,0,0.8)',
    darkGrey: 'rgba(0,0,0,0.5)',
    mediumGrey: 'rgba(0,0,0,0.25)',
    lightGrey: 'rgba(25,118,210,0.1)',
    background: 'rgba(255,255,255,0.9)',
};

export default function GraphingRenderer({
    question,
    handleUpdateUserResponse,
    loadedUserResponse,
    hideHelp,
    maxBoxWidth,
    maxBoxHeight,
    options = defaultOptions,
}: GraphingRendererProps) {
    const {
        disabled = false,
        hidePrompt = false,
        showLanguageToggle = false,
        showAudioButtons = false,
        showScoring = false,
        language = 'en',
    } = options;

    const [error, setError] = useState<string | null>(null);

    const BOX_MAX_WIDTH = maxBoxWidth ?? 600;
    const BOX_MAX_HEIGHT = maxBoxHeight ?? 600;

    const [internalLanguage, setInternalLanguage] = useState<'en' | 'es'>(language);
    useEffect(() => {
        setInternalLanguage(language);
    }, [language]);

    const [studentPoints, setStudentPoints] = useState<StudentPoint[]>([]);
    const [studentLines, setStudentLines] = useState<StudentLine[]>([]);

    const [labelEditor, setLabelEditor] = useState<LabelEditorState>(null);
    const [labelDraft, setLabelDraft] = useState<string>('');

    const [lineEditor, setLineEditor] = useState<LineEditorState>(null);
    const [lineLabelDraft, setLineLabelDraft] = useState<string>('');
    const [lineStyleDraft, setLineStyleDraft] = useState<StudentLine['style']>('segment');

    const isMouseDownRef = useRef(false);
    const mouseDownPxRef = useRef<{ sx: number; sy: number } | null>(null);

    const [dragPreview, setDragPreview] = useState<{
        x1: number;
        y1: number;
        x2: number;
        y2: number;
    } | null>(null);

    // Use if scoring needs to be handled internally
    const [userResponse, setUserResponse] = useState('');
    if (!handleUpdateUserResponse) {
        handleUpdateUserResponse = setUserResponse;
    }

    // Manage save data
    const userInteractedRef = useRef(false);
    useEffect(() => {
        if (!loadedUserResponse) return;
        try {
            const parsed = JSON.parse(loadedUserResponse);
            if (Array.isArray(parsed.points)) setStudentPoints(parsed.points);
            if (Array.isArray(parsed.lines)) setStudentLines(parsed.lines);
        } catch (e) {
            console.error('Invalid loadedUserResponse JSON:', e);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const payload =
            studentPoints.length || studentLines.length
                ? JSON.stringify({ points: studentPoints, lines: studentLines })
                : '';
        handleUpdateUserResponse(payload, userInteractedRef.current);
        userInteractedRef.current = false;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [studentPoints, studentLines]);

    const hiddenAnswerLabels = (() => {
        const labels = new Set<string>();
        question.points.forEach((p) => {
            if (p.isHidden && p.label) labels.add(p.label.trim());
        });
        question.lines.forEach((ln) => {
            if (ln.isHidden && ln.label) labels.add(ln.label.trim());
        });
        return Array.from(labels);
    })();

    // Points duplicated by (x,y) regardless of label
    const duplicatePointKeys = useMemo(() => {
        const counts = new Map<string, number>();
        for (const p of studentPoints) {
            const key = ptKey(p.x, p.y);
            counts.set(key, (counts.get(key) ?? 0) + 1);
        }
        const dups = new Set<string>();
        counts.forEach((c, k) => {
            if (c > 1) dups.add(k);
        });
        return dups; // keys like "-16|-3"
    }, [studentPoints]);

    const containerRef = useRef<HTMLDivElement | null>(null);
    const [containerWidth, setContainerWidth] = useState(0);

    useLayoutEffect(() => {
        if (!containerRef.current) return;
        const ro = new ResizeObserver((entries) => {
            const entry = entries[0];
            setContainerWidth(Math.floor(entry.contentRect.width));
        });
        ro.observe(containerRef.current);
        return () => ro.disconnect();
    }, []);

    const targetSize = 600;
    const fitByWidth = containerWidth > 0 ? containerWidth : targetSize;
    const renderSize = Math.min(
        targetSize,
        fitByWidth,
        BOX_MAX_WIDTH ?? targetSize,
        BOX_MAX_HEIGHT ?? targetSize
    );

    const renderW = renderSize;
    const renderH = renderSize;

    const { minX, maxX, minY, maxY, snapX, snapY } = question.axis;
    const minXn = Number(minX);
    const maxXn = Number(maxX);
    const minYn = Number(minY);
    const maxYn = Number(maxY);
    const snapXn = Number(snapX);
    const snapYn = Number(snapY);

    const { worldToScreen, screenToWorld } = useMemo(() => {
        const spanX = maxXn - minXn || 1;
        const spanY = maxYn - minYn || 1;
        const widthPx = renderW;
        const heightPx = renderH;
        function worldToScreen(x: number, y: number) {
            const sx = ((x - minXn) / spanX) * widthPx;
            const sy = heightPx - ((y - minYn) / spanY) * heightPx;
            return { sx, sy };
        }
        function screenToWorld(sx: number, sy: number) {
            const x = minXn + (sx / widthPx) * spanX;
            const y = minYn + ((heightPx - sy) / heightPx) * spanY;
            return { x, y };
        }
        return { worldToScreen, screenToWorld };
    }, [minXn, maxXn, minYn, maxYn, renderW, renderH]);

    const gridTicks = useMemo(
        () => buildGridTicks(minXn, maxXn, minYn, maxYn, snapXn, snapYn, GRID_CFG.minorPerMajor),
        [minXn, maxXn, minYn, maxYn, snapXn, snapYn]
    );

    const gridPlan = useMemo(() => {
        const { xsMajor, ysMajor, xsMinor, ysMinor, stepX, stepY } = gridTicks;

        const spanX = Math.max(1e-9, maxXn - minXn);
        const spanY = Math.max(1e-9, maxYn - minYn);
        const pxPerUnitX = renderW / spanX;
        const pxPerUnitY = renderH / spanY;

        const majorSpacingX = pxPerUnitX * stepX;
        const majorSpacingY = pxPerUnitY * stepY;

        const majStrideX = Math.max(
            1,
            Math.ceil(GRID_CFG.minMajorPx / Math.max(1e-9, majorSpacingX))
        );
        const majStrideY = Math.max(
            1,
            Math.ceil(GRID_CFG.minMajorPx / Math.max(1e-9, majorSpacingY))
        );

        const majorX = xsMajor.filter(
            (x) => !isApproximatelyZero(x) && onStride(x, stepX, majStrideX, 0)
        );
        const majorY = ysMajor.filter(
            (y) => !isApproximatelyZero(y) && onStride(y, stepY, majStrideY, 0)
        );

        const needStrideX = Math.max(1, Math.ceil(majorX.length / GRID_CFG.maxLabelsPerAxis));
        const needStrideY = Math.max(1, Math.ceil(majorY.length / GRID_CFG.maxLabelsPerAxis));

        const labelStrideX = majStrideX * needStrideX;
        const labelStrideY = majStrideY * needStrideY;

        const labelXs = xsMajor.filter(
            (x) =>
                !isApproximatelyZero(x) &&
                onStride(x, stepX, majStrideX, 0) &&
                onStride(x, stepX, labelStrideX, 0)
        );
        const labelYs = ysMajor.filter(
            (y) =>
                !isApproximatelyZero(y) &&
                onStride(y, stepY, majStrideY, 0) &&
                onStride(y, stepY, labelStrideY, 0)
        );

        // MINORS: only when majors aren’t already thinned, and keep a min pixel gap
        const minorPerMajor = Math.max(1, GRID_CFG.minorPerMajor | 0);
        const minorSpacingX = majorSpacingX / minorPerMajor;
        const minorSpacingY = majorSpacingY / minorPerMajor;

        const minStrideX = Math.max(
            1,
            Math.ceil(GRID_CFG.minMinorPx / Math.max(1e-9, minorSpacingX))
        );
        const minStrideY = Math.max(
            1,
            Math.ceil(GRID_CFG.minMinorPx / Math.max(1e-9, minorSpacingY))
        );

        const showMinorX =
            GRID_CFG.showMinor &&
            majStrideX === 1 &&
            minorSpacingX * minStrideX >= GRID_CFG.minMinorPx;
        const showMinorY =
            GRID_CFG.showMinor &&
            majStrideY === 1 &&
            minorSpacingY * minStrideY >= GRID_CFG.minMinorPx;

        const minorX = showMinorX
            ? xsMinor.filter((x) => onStride(x, stepX / minorPerMajor, minStrideX, 0))
            : [];
        const minorY = showMinorY
            ? ysMinor.filter((y) => onStride(y, stepY / minorPerMajor, minStrideY, 0))
            : [];

        return { majorX, majorY, minorX, minorY, labelXs, labelYs };
    }, [gridTicks, renderW, renderH, minXn, maxXn, minYn, maxYn]);

    const svgRef = useRef<SVGSVGElement | null>(null);

    const getUnusedLabels = useCallback(() => {
        const used = new Set([
            ...studentPoints.map((p) => p.label.trim()),
            ...studentLines.map((l) => l.label.trim()),
        ]);
        return hiddenAnswerLabels.filter((lbl) => !used.has(lbl));
    }, [studentPoints, studentLines, hiddenAnswerLabels]);

    const getAllowedLabelsForPointIndex = useCallback(
        (idx: number) => {
            const usedByOthers = new Set([
                ...studentPoints.filter((_, i) => i !== idx).map((p) => p.label.trim()),
                ...studentLines.map((l) => l.label.trim()),
            ]);
            const current = studentPoints[idx]?.label?.trim();
            return hiddenAnswerLabels.filter((lbl) => lbl === current || !usedByOthers.has(lbl));
        },
        [studentPoints, studentLines, hiddenAnswerLabels]
    );

    const getAllowedLabelsForLineIndex = useCallback(
        (idx: number) => {
            const usedByOthers = new Set([
                ...studentLines.filter((_, i) => i !== idx).map((l) => l.label.trim()),
                ...studentPoints.map((p) => p.label.trim()),
            ]);
            const current = studentLines[idx]?.label?.trim();
            return hiddenAnswerLabels.filter((lbl) => lbl === current || !usedByOthers.has(lbl));
        },
        [studentLines, studentPoints, hiddenAnswerLabels]
    );

    const allowedItemTypes = useMemo(() => {
        const { allowPoints, allowSegments, allowRays, allowLines } = question.additionalMeta || {};
        const types: ('point' | 'segment' | 'ray' | 'line')[] = [];
        if (allowPoints) types.push('point');
        if (allowSegments) types.push('segment');
        if (allowRays) types.push('ray');
        if (allowLines) types.push('line');
        return types;

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        question.additionalMeta.allowPoints,
        question.additionalMeta.allowSegments,
        question.additionalMeta.allowLines,
        question.additionalMeta.allowRays,
    ]);

    // Mouse: Determine point vs line
    const DRAG_THRESH_SQ = 9;
    const interactionsDisabled = !!labelEditor || !!lineEditor || disabled;

    const handleMouseDown = useCallback(
        (e: React.MouseEvent<SVGSVGElement>) => {
            if ((e.nativeEvent as MouseEvent).button !== 0) {
                // not left
                e.preventDefault();
                e.stopPropagation();
                return;
            }

            if (interactionsDisabled) return;
            if (!svgRef.current) return;
            isMouseDownRef.current = true;
            const rect = svgRef.current.getBoundingClientRect();
            const sx = e.clientX - rect.left;
            const sy = e.clientY - rect.top;
            mouseDownPxRef.current = { sx, sy };
            setDragPreview(null);
        },
        [interactionsDisabled]
    );

    const handleMouseMove = useCallback(
        (e: React.MouseEvent<SVGSVGElement>) => {
            if (interactionsDisabled) return;
            if (!isMouseDownRef.current || !svgRef.current || !mouseDownPxRef.current) return;

            // Do not show preview if no labels
            if (getUnusedLabels().length === 0) {
                setDragPreview(null);
                return;
            }

            const rect = svgRef.current.getBoundingClientRect();
            const sx = e.clientX - rect.left;
            const sy = e.clientY - rect.top;

            const dx = sx - mouseDownPxRef.current.sx;
            const dy = sy - mouseDownPxRef.current.sy;
            if (dx * dx + dy * dy < DRAG_THRESH_SQ) {
                setDragPreview(null);
                return;
            }

            const startWorld = screenToWorld(mouseDownPxRef.current.sx, mouseDownPxRef.current.sy);
            const endWorld = screenToWorld(sx, sy);

            const x1 = parseFloat(snapTo(startWorld.x, snapXn).toFixed(4));
            const y1 = parseFloat(snapTo(startWorld.y, snapYn).toFixed(4));
            const x2 = parseFloat(snapTo(endWorld.x, snapXn).toFixed(4));
            const y2 = parseFloat(snapTo(endWorld.y, snapYn).toFixed(4));

            setDragPreview({ x1, y1, x2, y2 });
        },
        [interactionsDisabled, getUnusedLabels, screenToWorld, snapXn, snapYn]
    );

    const handleMouseUp = useCallback(
        (e: React.MouseEvent<SVGSVGElement>) => {
            if ((e.nativeEvent as MouseEvent).button !== 0) {
                // not left
                e.preventDefault();
                e.stopPropagation();
                return;
            }

            if (interactionsDisabled) return;
            if (!svgRef.current || !mouseDownPxRef.current) {
                isMouseDownRef.current = false;
                setDragPreview(null);
                return;
            }
            const rect = svgRef.current.getBoundingClientRect();
            const sx = e.clientX - rect.left;
            const sy = e.clientY - rect.top;

            const dx = sx - mouseDownPxRef.current.sx;
            const dy = sy - mouseDownPxRef.current.sy;
            const wasDrag = dx * dx + dy * dy >= DRAG_THRESH_SQ;

            isMouseDownRef.current = false;

            if (wasDrag) {
                const available = getUnusedLabels();
                if (available.length === 0) {
                    setDragPreview(null);
                    return;
                }

                const startWorld = screenToWorld(
                    mouseDownPxRef.current.sx,
                    mouseDownPxRef.current.sy
                );
                const endWorld = screenToWorld(sx, sy);

                const x1 = parseFloat(snapTo(startWorld.x, snapXn).toFixed(4));
                const y1 = parseFloat(snapTo(startWorld.y, snapYn).toFixed(4));
                const x2 = parseFloat(snapTo(endWorld.x, snapXn).toFixed(4));
                const y2 = parseFloat(snapTo(endWorld.y, snapYn).toFixed(4));

                setLineLabelDraft(available[0] ?? '');
                let firstAvailableType = allowedItemTypes[0];
                if (firstAvailableType === 'point') {
                    // Default to segment if point is selected for line drawing
                    firstAvailableType = allowedItemTypes[1];
                }
                const typeToUse = firstAvailableType || 'segment';
                setLineStyleDraft(typeToUse as StudentLine['style']);
                setLineEditor({ mode: 'new', x1, y1, x2, y2, screenX: sx, screenY: sy });
                setDragPreview({ x1, y1, x2, y2 });
            } else {
                const available = getUnusedLabels();
                if (available.length === 0) {
                    setDragPreview(null);
                    return;
                }

                const { x: rawX, y: rawY } = screenToWorld(sx, sy);
                const snappedX = parseFloat(snapTo(rawX, snapXn).toFixed(4));
                const snappedY = parseFloat(snapTo(rawY, snapYn).toFixed(4));

                const pending: PendingPoint = {
                    x: snappedX,
                    y: snappedY,
                    screenX: sx,
                    screenY: sy,
                };
                setLabelDraft(available[0] ?? '');
                setLabelEditor({ mode: 'new', point: pending });
                setDragPreview(null);
            }
        },
        [interactionsDisabled, getUnusedLabels, screenToWorld, snapXn, snapYn, allowedItemTypes]
    );

    // Point editing
    const commitNewPoint = useCallback(() => {
        if (!labelEditor || labelEditor.mode !== 'new' || !labelDraft) return;
        const used = new Set([
            ...studentPoints.map((p) => p.label.trim()),
            ...studentLines.map((l) => l.label.trim()),
        ]);
        if (used.has(labelDraft.trim())) return;

        const newPt: StudentPoint = {
            x: labelEditor.point.x,
            y: labelEditor.point.y,
            label: labelDraft,
        };
        setStudentPoints((prev) => [...prev, newPt]);
        setLabelEditor(null);
        userInteractedRef.current = true;
    }, [labelEditor, labelDraft, studentPoints, studentLines]);

    const commitEditedPoint = useCallback(() => {
        if (!labelEditor || labelEditor.mode !== 'edit' || !labelDraft) return;
        const idx = labelEditor.index!;
        const allowed = getAllowedLabelsForPointIndex(idx);
        if (!allowed.includes(labelDraft)) return;

        setStudentPoints((prev) => {
            const next = prev.slice();
            next[idx] = { ...next[idx], label: labelDraft };
            return next;
        });
        setLabelEditor(null);
        userInteractedRef.current = true;
    }, [labelEditor, labelDraft, getAllowedLabelsForPointIndex]);

    const removeEditedPoint = useCallback(() => {
        if (!labelEditor || labelEditor.mode !== 'edit') return;
        const idx = labelEditor.index!;
        setStudentPoints((prev) => {
            const next = prev.slice();
            next.splice(idx, 1);
            return next;
        });
        setLabelEditor(null);
        userInteractedRef.current = true;
    }, [labelEditor]);

    // Line editing
    const commitNewLine = useCallback(() => {
        if (!lineEditor || lineEditor.mode !== 'new' || !lineLabelDraft) return;
        const used = new Set([
            ...studentLines.map((l) => l.label.trim()),
            ...studentPoints.map((p) => p.label.trim()),
        ]);
        if (used.has(lineLabelDraft.trim())) return;

        const newLn: StudentLine = {
            x1: lineEditor.x1,
            y1: lineEditor.y1,
            x2: lineEditor.x2,
            y2: lineEditor.y2,
            label: lineLabelDraft,
            style: lineStyleDraft,
        };
        setStudentLines((prev) => [...prev, newLn]);
        setLineEditor(null);
        setDragPreview(null);
        userInteractedRef.current = true;
    }, [lineEditor, lineLabelDraft, lineStyleDraft, studentLines, studentPoints]);

    const commitEditedLine = useCallback(() => {
        if (!lineEditor || lineEditor.mode !== 'edit' || !lineLabelDraft) return;
        const idx = lineEditor.index!;
        const allowed = getAllowedLabelsForLineIndex(idx);
        if (!allowed.includes(lineLabelDraft)) return;

        setStudentLines((prev) => {
            const next = prev.slice();
            next[idx] = { ...next[idx], label: lineLabelDraft, style: lineStyleDraft };
            return next;
        });
        setLineEditor(null);
        setDragPreview(null);
        userInteractedRef.current = true;
    }, [lineEditor, lineLabelDraft, lineStyleDraft, getAllowedLabelsForLineIndex]);

    const removeEditedLine = useCallback(() => {
        if (!lineEditor || lineEditor.mode !== 'edit') return;
        const idx = lineEditor.index!;
        setStudentLines((prev) => {
            const next = prev.slice();
            next.splice(idx, 1);
            return next;
        });
        setLineEditor(null);
        setDragPreview(null);
        userInteractedRef.current = true;
    }, [lineEditor]);

    // Render helpers
    function renderShownLines(lines: GraphLine[]) {
        return lines
            .filter((ln) => !ln.isHidden)
            .map((ln) => {
                const x1n = coerceNum(ln.x1),
                    y1n = coerceNum(ln.y1);
                const x2n = coerceNum(ln.x2),
                    y2n = coerceNum(ln.y2);
                if (x1n === null || y1n === null || x2n === null || y2n === null) {
                    return null;
                }

                const { ax, ay, bx, by } = extendEndpoints(
                    x1n,
                    y1n,
                    x2n,
                    y2n,
                    ln.style,
                    minXn,
                    maxXn,
                    minYn,
                    maxYn,
                    snapXn,
                    snapYn,
                    0.75
                );

                if (![ax, ay, bx, by].every(Number.isFinite)) return null;

                const p1 = worldToScreen(ax, ay);
                const p2 = worldToScreen(bx, by);
                if (![p1.sx, p1.sy, p2.sx, p2.sy].every(Number.isFinite)) return null;

                const stroke = colorPalette.darkGrey;
                const markerStart = ln.style === 'line' ? 'url(#arrow)' : undefined;
                const markerEnd =
                    ln.style === 'line' || ln.style === 'ray' ? 'url(#arrow)' : undefined;

                return (
                    <g key={ln.id}>
                        <line
                            x1={p1.sx}
                            y1={p1.sy}
                            x2={p2.sx}
                            y2={p2.sy}
                            stroke={stroke}
                            strokeWidth={2}
                            markerStart={markerStart}
                            markerEnd={markerEnd}
                            pointerEvents="none"
                        />
                        {ln.style !== 'line' && (
                            <>
                                <circle
                                    cx={p1.sx}
                                    cy={p1.sy}
                                    r={4}
                                    fill={stroke}
                                    stroke={colorPalette.mediumGrey}
                                    strokeWidth={1}
                                    pointerEvents="none"
                                />
                                {ln.style === 'segment' && (
                                    <circle
                                        cx={p2.sx}
                                        cy={p2.sy}
                                        r={4}
                                        fill={stroke}
                                        stroke={colorPalette.mediumGrey}
                                        strokeWidth={1}
                                        pointerEvents="none"
                                    />
                                )}
                            </>
                        )}
                        {ln.label && (
                            <text
                                x={(p1.sx + p2.sx) / 2}
                                y={(p1.sy + p2.sy) / 2}
                                textAnchor="middle"
                                dominantBaseline="central"
                                fontSize={14}
                                fontWeight={700}
                                fill={stroke}
                                stroke={colorPalette.background}
                                strokeWidth={3}
                                paintOrder="stroke fill"
                                style={{ userSelect: 'none' }}
                                pointerEvents="none"
                            >
                                {ln.label}
                            </text>
                        )}
                    </g>
                );
            });
    }

    function renderShownPoints(points: GraphPoint[]) {
        return points
            .filter((pt) => !pt.isHidden)
            .map((pt) => {
                const { sx, sy } = worldToScreen(pt.x, pt.y);
                const stroke = colorPalette.darkGrey;

                return (
                    <g key={pt.id}>
                        <circle
                            cx={sx}
                            cy={sy}
                            r={4}
                            fill={stroke}
                            stroke={colorPalette.darkGrey}
                            strokeWidth={1}
                            pointerEvents="none"
                        />
                        <text
                            x={sx + 6}
                            y={sy - 6}
                            fontSize={14}
                            fontWeight={700}
                            fill={stroke}
                            stroke={colorPalette.background}
                            strokeWidth={3}
                            paintOrder="stroke fill"
                            style={{ userSelect: 'none' }}
                            pointerEvents="none"
                        >
                            {pt.label}
                        </text>
                    </g>
                );
            });
    }

    function renderStudentLines() {
        return studentLines.map((ln, idx) => {
            const x1n = coerceNum(ln.x1),
                y1n = coerceNum(ln.y1);
            const x2n = coerceNum(ln.x2),
                y2n = coerceNum(ln.y2);
            if (x1n === null || y1n === null || x2n === null || y2n === null) return null;

            const { ax, ay, bx, by } = extendEndpoints(
                x1n,
                y1n,
                x2n,
                y2n,
                ln.style,
                minXn,
                maxXn,
                minYn,
                maxYn,
                snapXn,
                snapYn,
                0.75
            );
            if (![ax, ay, bx, by].every(Number.isFinite)) return null;

            const p1 = worldToScreen(ax, ay);
            const p2 = worldToScreen(bx, by);
            if (![p1.sx, p1.sy, p2.sx, p2.sy].every(Number.isFinite)) return null;

            const stroke = colorPalette.green;
            const markerStart = ln.style === 'line' ? 'url(#arrow)' : undefined;
            const markerEnd = ln.style === 'line' || ln.style === 'ray' ? 'url(#arrow)' : undefined;

            const onCtxMenu = disabled
                ? () => null
                : (e: React.MouseEvent) => openLineMenuAtEvent(e, idx, ln);

            return (
                <g
                    key={`stu-ln-${idx}`}
                    style={{ color: stroke, cursor: disabled ? 'not-allowed' : 'pointer' }}
                >
                    {/* invisible hit padding for wider click area */}
                    <line
                        x1={p1.sx}
                        y1={p1.sy}
                        x2={p2.sx}
                        y2={p2.sy}
                        stroke="transparent"
                        strokeWidth={14}
                        pointerEvents="stroke"
                        onContextMenu={onCtxMenu}
                    />
                    {/* visible line */}
                    <line
                        x1={p1.sx}
                        y1={p1.sy}
                        x2={p2.sx}
                        y2={p2.sy}
                        stroke={stroke}
                        strokeWidth={2}
                        markerStart={markerStart}
                        markerEnd={markerEnd}
                        pointerEvents="none"
                    />
                    {ln.style !== 'line' && (
                        <>
                            <circle
                                cx={p1.sx}
                                cy={p1.sy}
                                r={4}
                                fill={stroke}
                                stroke={colorPalette.black}
                                strokeWidth={1}
                                pointerEvents="none"
                            />
                            {ln.style === 'segment' && (
                                <circle
                                    cx={p2.sx}
                                    cy={p2.sy}
                                    r={4}
                                    fill={stroke}
                                    stroke={colorPalette.black}
                                    strokeWidth={1}
                                    pointerEvents="none"
                                />
                            )}
                        </>
                    )}
                    <text
                        x={(p1.sx + p2.sx) / 2}
                        y={(p1.sy + p2.sy) / 2}
                        textAnchor="middle"
                        dominantBaseline="central"
                        fontSize={14}
                        fontWeight={700}
                        fill={stroke}
                        stroke="#fff"
                        strokeWidth={3}
                        paintOrder="stroke fill"
                        style={{ userSelect: 'none' }}
                        onContextMenu={onCtxMenu}
                    >
                        {ln.label}
                    </text>
                </g>
            );
        });
    }

    function renderStudentPoints() {
        const dups = duplicatePointKeys;
        return studentPoints.map((pt, idx) => {
            const { sx, sy } = worldToScreen(pt.x, pt.y);
            const isDup = dups.has(ptKey(pt.x, pt.y));
            const stroke = isDup ? colorPalette.orange : colorPalette.green;

            const onCtxMenu = disabled
                ? () => null
                : (e: React.MouseEvent) => openPointMenuAtEvent(e, idx, pt);

            return (
                <g
                    key={`stu-${idx}`}
                    onContextMenu={onCtxMenu}
                    style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
                >
                    {/* invisible hit padding for wider click area */}
                    <circle cx={sx} cy={sy} r={12} fill="transparent" pointerEvents="all" />
                    {/* visible point */}
                    <circle
                        cx={sx}
                        cy={sy}
                        r={4}
                        fill={stroke}
                        stroke={colorPalette.black}
                        strokeWidth={1}
                        pointerEvents="none"
                    />
                    <text
                        x={sx + 6}
                        y={sy - 6}
                        fontSize={14}
                        fontWeight={700}
                        fill={stroke}
                        stroke={colorPalette.background}
                        strokeWidth={3}
                        paintOrder="stroke fill"
                        style={{ userSelect: 'none' }}
                        onContextMenu={onCtxMenu}
                    >
                        {pt.label}
                    </text>
                </g>
            );
        });
    }

    function renderGrid() {
        const { majorX, majorY, minorX, minorY, labelXs, labelYs } = gridPlan;

        const xAxisLeft = worldToScreen(minX, 0);
        const xAxisRight = worldToScreen(maxX, 0);
        const yAxisBottom = worldToScreen(0, minY);
        const yAxisTop = worldToScreen(0, maxY);

        return (
            <g shapeRendering="crispEdges">
                {/* Minor grid */}
                {minorX.map((x) => {
                    const top = worldToScreen(x, maxY);
                    const bot = worldToScreen(x, minY);
                    return (
                        <line
                            key={`v-min-${x}`}
                            x1={top.sx}
                            y1={top.sy}
                            x2={bot.sx}
                            y2={bot.sy}
                            stroke={colorPalette.lightGrey}
                            strokeWidth={1}
                        />
                    );
                })}
                {minorY.map((y) => {
                    const left = worldToScreen(minX, y);
                    const right = worldToScreen(maxX, y);
                    return (
                        <line
                            key={`h-min-${y}`}
                            x1={left.sx}
                            y1={left.sy}
                            x2={right.sx}
                            y2={right.sy}
                            stroke={colorPalette.lightGrey}
                            strokeWidth={1}
                        />
                    );
                })}

                {/* Major grid (non-axis) */}
                {majorX.map((x) => {
                    if (isApproximatelyZero(x)) return null;
                    const top = worldToScreen(x, maxY);
                    const bot = worldToScreen(x, minY);
                    return (
                        <line
                            key={`v-maj-${x}`}
                            x1={top.sx}
                            y1={top.sy}
                            x2={bot.sx}
                            y2={bot.sy}
                            stroke={colorPalette.mediumGrey}
                            strokeWidth={1}
                        />
                    );
                })}
                {majorY.map((y) => {
                    if (isApproximatelyZero(y)) return null;
                    const left = worldToScreen(minX, y);
                    const right = worldToScreen(maxX, y);
                    return (
                        <line
                            key={`h-maj-${y}`}
                            x1={left.sx}
                            y1={left.sy}
                            x2={right.sx}
                            y2={right.sy}
                            stroke={colorPalette.mediumGrey}
                            strokeWidth={1}
                        />
                    );
                })}

                {/* Axes with arrowheads */}
                <line
                    x1={xAxisLeft.sx}
                    y1={xAxisLeft.sy}
                    x2={xAxisRight.sx}
                    y2={xAxisRight.sy}
                    stroke={colorPalette.black}
                    strokeWidth={2}
                    markerStart="url(#axis-arrow)"
                    markerEnd="url(#axis-arrow)"
                />
                <line
                    x1={yAxisBottom.sx}
                    y1={yAxisBottom.sy}
                    x2={yAxisTop.sx}
                    y2={yAxisTop.sy}
                    stroke={colorPalette.black}
                    strokeWidth={2}
                    markerStart="url(#axis-arrow)"
                    markerEnd="url(#axis-arrow)"
                />

                {/* Tick labels (only on visible majors, capped) */}
                {GRID_CFG.showLabels &&
                    labelXs.map((x) => {
                        const p = worldToScreen(x, 0);
                        return (
                            <text
                                key={`tx-${x}`}
                                x={p.sx}
                                y={xAxisLeft.sy + 14}
                                fontSize={GRID_CFG.fontSize}
                                textAnchor="middle"
                                fill={colorPalette.black}
                                style={{ userSelect: 'none', pointerEvents: 'none' }}
                            >
                                {formatTick(x)}
                            </text>
                        );
                    })}
                {GRID_CFG.showLabels &&
                    labelYs.map((y) => {
                        const p = worldToScreen(0, y);
                        return (
                            <text
                                key={`ty-${y}`}
                                x={yAxisBottom.sx - 10}
                                y={p.sy + 4}
                                fontSize={GRID_CFG.fontSize}
                                textAnchor="end"
                                fill={colorPalette.black}
                                style={{ userSelect: 'none', pointerEvents: 'none' }}
                            >
                                {formatTick(y)}
                            </text>
                        );
                    })}

                {/* Single origin label "0" */}
                {GRID_CFG.showLabels && (
                    <text
                        x={worldToScreen(0, 0).sx + 3}
                        y={worldToScreen(0, 0).sy - 3}
                        fontSize={GRID_CFG.fontSize}
                        textAnchor="start"
                        fill={colorPalette.black}
                        style={{ userSelect: 'none', pointerEvents: 'none' }}
                    >
                        0
                    </text>
                )}

                {/* Axis labels */}
                {AXIS_LABELS.x && (
                    <text
                        x={xAxisRight.sx + AXIS_LABELS.offset}
                        y={xAxisRight.sy}
                        fontSize={AXIS_LABELS.fontSize}
                        textAnchor="start"
                        fill={colorPalette.black}
                        style={{ userSelect: 'none', pointerEvents: 'none', fontStyle: 'italic' }}
                    >
                        {AXIS_LABELS.x}
                    </text>
                )}
                {AXIS_LABELS.y && (
                    <text
                        x={yAxisTop.sx}
                        y={yAxisTop.sy - AXIS_LABELS.offset}
                        fontSize={AXIS_LABELS.fontSize}
                        textAnchor="middle"
                        fill={colorPalette.black}
                        style={{ userSelect: 'none', pointerEvents: 'none', fontStyle: 'italic' }}
                    >
                        {AXIS_LABELS.y}
                    </text>
                )}
            </g>
        );
    }

    // Popups
    const POPUP_W = 280;
    const POPUP_MARGIN = 8;

    const clampPopupLeft = (rawLeft: number, hostWidth: number) => {
        const min = POPUP_MARGIN;
        const max = hostWidth - POPUP_W - POPUP_MARGIN;
        return Math.max(min, Math.min(rawLeft, max, POPUP_W));
    };

    const pointPopup = (() => {
        if (!labelEditor) return null;

        const rawLeft =
            (labelEditor.mode === 'new' ? labelEditor.point.screenX : labelEditor.screenX) + 8;
        const rawTop =
            (labelEditor.mode === 'new' ? labelEditor.point.screenY : labelEditor.screenY) - 8;

        const left = clampPopupLeft(rawLeft, renderW);
        const top = rawTop;

        const allowed =
            labelEditor.mode === 'new'
                ? getUnusedLabels()
                : getAllowedLabelsForPointIndex(labelEditor.index!);

        return (
            <PointLabelPopup
                left={left}
                top={top}
                width={POPUP_W}
                labelDraft={labelDraft}
                setLabelDraft={setLabelDraft}
                allowedLabels={allowed}
                mode={labelEditor.mode}
                onRemove={labelEditor.mode === 'edit' ? removeEditedPoint : undefined}
                onSave={labelEditor.mode === 'new' ? commitNewPoint : commitEditedPoint}
                onCancel={() => setLabelEditor(null)}
            />
        );
    })();

    const linePopup = (() => {
        const areNoTypesAllowed =
            !allowedItemTypes.includes('segment') &&
            !allowedItemTypes.includes('ray') &&
            !allowedItemTypes.includes('line');
        if (!lineEditor || areNoTypesAllowed) return null;

        const rawLeft = lineEditor.screenX + 8;
        const rawTop = lineEditor.screenY - 8;

        const left = clampPopupLeft(rawLeft, renderW);
        const top = rawTop;

        const allowedLabels =
            lineEditor.mode === 'new'
                ? getUnusedLabels()
                : getAllowedLabelsForLineIndex(lineEditor.index!);

        return (
            <LineEditorPopup
                left={left}
                top={top}
                width={POPUP_W}
                labelDraft={lineLabelDraft}
                setLabelDraft={setLineLabelDraft}
                styleDraft={lineStyleDraft}
                setStyleDraft={setLineStyleDraft}
                allowedLabels={allowedLabels}
                allowedItemTypes={allowedItemTypes}
                mode={lineEditor.mode}
                onRemove={lineEditor.mode === 'edit' ? removeEditedLine : undefined}
                onSave={lineEditor.mode === 'new' ? commitNewLine : commitEditedLine}
                onCancel={() => {
                    setLineEditor(null);
                    setDragPreview(null);
                }}
            />
        );
    })();

    // Previews
    function renderPointPreview() {
        if (!labelEditor || !allowedItemTypes.includes('point')) return null;

        if (labelEditor.mode === 'new') {
            const { sx, sy } = worldToScreen(labelEditor.point.x, labelEditor.point.y);
            return (
                <g pointerEvents="none">
                    <circle
                        cx={sx}
                        cy={sy}
                        r={12}
                        fill="transparent"
                        stroke={colorPalette.mediumGrey}
                        strokeWidth={1.5}
                        strokeDasharray="4 4"
                    />
                    <circle cx={sx} cy={sy} r={4} fill={colorPalette.black} />
                    {labelDraft && (
                        <text
                            x={sx + 8}
                            y={sy - 8}
                            fontSize={14}
                            fontWeight={700}
                            fill={colorPalette.black}
                            stroke={colorPalette.background}
                            strokeWidth={3}
                            paintOrder="stroke fill"
                        >
                            {labelDraft}
                        </text>
                    )}
                </g>
            );
        }

        const cur = studentPoints[labelEditor.index!];
        if (!cur) return null;

        const { sx, sy } = worldToScreen(cur.x, cur.y);
        const label = labelDraft || cur.label;

        return (
            <g pointerEvents="none">
                <circle
                    cx={sx}
                    cy={sy}
                    r={14}
                    fill="transparent"
                    stroke={colorPalette.mediumGrey}
                    strokeWidth={2}
                />
                <circle
                    cx={sx}
                    cy={sy}
                    r={4}
                    fill={colorPalette.green}
                    stroke={colorPalette.black}
                    strokeWidth={1}
                />
                <text
                    x={sx + 8}
                    y={sy - 8}
                    fontSize={14}
                    fontWeight={700}
                    fill={colorPalette.black}
                    stroke={colorPalette.background}
                    strokeWidth={3}
                    paintOrder="stroke fill"
                >
                    {label}
                </text>
            </g>
        );
    }

    function renderLinePreview() {
        const src =
            lineEditor && lineEditor.mode === 'new'
                ? { x1: lineEditor.x1, y1: lineEditor.y1, x2: lineEditor.x2, y2: lineEditor.y2 }
                : dragPreview;

        if (!src) return null;

        const p1 = worldToScreen(src.x1, src.y1);
        const p2 = worldToScreen(src.x2, src.y2);
        const stroke = colorPalette.darkGrey;

        // Only show arrowheads if the line editor is open (i.e., a style is chosen)
        const styleForPreview: 'segment' | 'ray' | 'line' | null =
            lineEditor && lineEditor.mode === 'new' ? lineStyleDraft : null;

        const markerStart = styleForPreview === 'line' ? 'url(#arrow)' : undefined;
        const markerEnd =
            styleForPreview === 'line' || styleForPreview === 'ray' ? 'url(#arrow)' : undefined;

        return (
            <line
                x1={p1.sx}
                y1={p1.sy}
                x2={p2.sx}
                y2={p2.sy}
                stroke={stroke}
                strokeWidth={2}
                strokeDasharray="4 4"
                markerStart={markerStart}
                markerEnd={markerEnd}
                pointerEvents="none"
            />
        );
    }

    // Context menu helpers
    const openPointMenuAtEvent = useCallback(
        (e: React.MouseEvent, idx: number, pt: StudentPoint) => {
            e.preventDefault();
            e.stopPropagation();
            if (!svgRef.current) return;
            const rect = svgRef.current.getBoundingClientRect();
            const sx = e.clientX - rect.left;
            const sy = e.clientY - rect.top;

            const allowed = getAllowedLabelsForPointIndex(idx);
            const currentLabel = allowed.includes(pt.label) ? pt.label : (allowed[0] ?? pt.label);

            setLabelDraft(currentLabel || '');
            setLabelEditor({ mode: 'edit', index: idx, screenX: sx, screenY: sy });
        },
        [getAllowedLabelsForPointIndex]
    );

    const openLineMenuAtEvent = useCallback(
        (e: React.MouseEvent, idx: number, ln: StudentLine) => {
            e.preventDefault();
            e.stopPropagation();
            if (!svgRef.current) return;
            const rect = svgRef.current.getBoundingClientRect();
            const sx = e.clientX - rect.left;
            const sy = e.clientY - rect.top;

            const allowed = getAllowedLabelsForLineIndex(idx);
            const currentLabel = allowed.includes(ln.label) ? ln.label : (allowed[0] ?? ln.label);

            setLineLabelDraft(currentLabel || '');
            setLineStyleDraft(ln.style);
            setLineEditor({ mode: 'edit', index: idx, screenX: sx, screenY: sy });
        },
        [getAllowedLabelsForLineIndex]
    );

    const [showHelp, setShowHelp] = useState(false);
    const renderLegend = useCallback(() => {
        if (hideHelp) return null;

        const helpText =
            internalLanguage === 'en'
                ? [
                      'Click to add a point.',
                      'Click and drag to add a line, segment, or ray.',
                      'Right click an item to edit or delete it.',
                  ]
                : [
                      'Haga clic para agregar un punto.',
                      'Haga clic y arrastre para agregar una línea, segmento o rayo.',
                      'Haga clic derecho en un elemento para editarlo o eliminarlo.',
                  ];

        return (
            <>
                <div
                    role="note"
                    aria-label="Graph legend"
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 16,
                        marginTop: 8,
                        cursor: 'pointer',
                        padding: '4px 6px 0px 0px',
                        borderRadius: 6,
                        width: 'fit-content',
                    }}
                >
                    <div style={{ position: 'relative', display: 'inline-block' }}>
                        <span
                            aria-label={
                                internalLanguage === 'en'
                                    ? 'Show graphing help'
                                    : 'Mostrar ayuda de graficación'
                            }
                            onMouseEnter={() => setShowHelp(true)}
                            onMouseLeave={() => setShowHelp(false)}
                            style={{
                                background: 'none',
                                border: 'none',
                                color: colorPalette.primary,
                                fontSize: 16,
                                padding: 0,
                            }}
                        >
                            {internalLanguage === 'en' ? 'Graphing Help' : 'Ayuda de graficación'}
                        </span>
                        {showHelp && (
                            <div
                                className="text-sm text-gray-700"
                                style={{
                                    position: 'absolute',
                                    left: '50%',
                                    top: '120%',
                                    transform: 'translateX(-50%)',
                                    zIndex: 100,
                                    background: colorPalette.background,
                                    border: `1px solid ${colorPalette.mediumGrey}`,
                                    borderRadius: 4,
                                    boxShadow: `0 2px 8px ${colorPalette.lightGrey}`,
                                    padding: '10px 14px',
                                    minWidth: 220,
                                    maxWidth: 320,
                                }}
                            >
                                {helpText.map((line, i) => (
                                    <p className={i < helpText.length - 1 ? 'mb-2' : ''} key={i}>
                                        {line}
                                    </p>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </>
        );
    }, [hideHelp, internalLanguage, showHelp]);

    const renderUnusedLabels = useCallback(() => {
        const labels = getUnusedLabels();
        if (!labels || hideHelp) {
            return null;
        }

        if (labels.length === 0) {
            return <span>All items have been graphed.</span>;
        }

        const maxItems = 24;

        const shown = labels.slice(0, Math.max(0, maxItems));
        const remaining = Math.max(0, labels.length - shown.length);

        const labelText =
            internalLanguage === 'en'
                ? 'Items left to graph:'
                : 'Elementos restantes para graficar:';

        return (
            <div className="mt-2 text-x" aria-label={labelText}>
                <div className="mb-1 font-medium">{labelText}</div>
                <div className="flex flex-wrap gap-2">
                    {shown.map((lbl) => (
                        <span key={lbl} className="px-2 py-1 border rounded bg-white text-xs">
                            {lbl}
                        </span>
                    ))}
                    {remaining > 0 && <em>+{remaining} more</em>}
                </div>
            </div>
        );
    }, [getUnusedLabels, internalLanguage, hideHelp]);

    return (
        <Stack spacing={2}>
            {showLanguageToggle && question.meta.isSpanishAllowed && (
                <div className="flex flex-row gap-x-8">
                    <div className="ml-auto">
                        <SimpleSwitch
                            name="toggleSpanish"
                            checked={internalLanguage === 'es'}
                            onChange={(e) => setInternalLanguage(e.target.checked ? 'es' : 'en')}
                            label="View Spanish"
                        />
                    </div>
                </div>
            )}

            {!hidePrompt && (
                <div
                    className="mb-4 flex flex-row items-end"
                    onContextMenu={(e) => {
                        if (options.onRendererContextMenu) {
                            e.preventDefault();
                            e.stopPropagation();
                            options.onRendererContextMenu(
                                e,
                                {
                                    language: internalLanguage,
                                    text: question.prompt,
                                    text_es: question.prompt_es,
                                },
                                []
                            );
                        }
                    }}
                >
                    <DynamicTextPreview
                        content={internalLanguage === 'en' ? question.prompt : question.prompt_es}
                    />
                    {showAudioButtons && (
                        <TTSButton
                            text={internalLanguage === 'en' ? question.prompt : question.prompt_es}
                            onError={setError}
                        />
                    )}
                </div>
            )}

            <Paper elevation={1} sx={{ p: 1 }}>
                <Box ref={containerRef} sx={{ width: '100%', maxWidth: BOX_MAX_WIDTH ?? 500 }}>
                    <Box
                        sx={{
                            display: 'inline-flex',
                            flexDirection: 'column',
                            position: 'relative',
                            border: `1px solid ${colorPalette.lightGrey}`,
                            backgroundColor: colorPalette.background,
                            userSelect: 'none',
                            maxWidth: '100%',
                        }}
                    >
                        <div className="pb-4 pl-4 gap-2 flex flex-col">
                            {renderLegend()}
                            {renderUnusedLabels()}
                        </div>

                        <svg
                            ref={svgRef}
                            width={renderW}
                            height={renderH}
                            style={{
                                display: 'block',
                                overflow: 'visible',
                                cursor:
                                    !interactionsDisabled && getUnusedLabels().length > 0
                                        ? 'crosshair'
                                        : 'not-allowed',
                            }}
                            onMouseDown={handleMouseDown}
                            onMouseMove={handleMouseMove}
                            onMouseUp={handleMouseUp}
                            onClick={(e) => {
                                if ((e.nativeEvent as MouseEvent).button !== 0) return;
                            }}
                            onContextMenu={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                            }}
                            shapeRendering={'crispEdges'}
                        >
                            {/* Arrow SVGs */}
                            <defs>
                                <marker
                                    id="arrow"
                                    viewBox="0 0 10 10"
                                    refX="8"
                                    refY="5"
                                    markerWidth="6"
                                    markerHeight="6"
                                    orient="auto-start-reverse"
                                    markerUnits="strokeWidth"
                                >
                                    <path d="M 0 0 L 10 5 L 0 10 z" fill="context-stroke" />
                                </marker>
                                <marker
                                    id="axis-arrow"
                                    viewBox="0 0 10 10"
                                    refX="8"
                                    refY="5"
                                    markerWidth="6"
                                    markerHeight="6"
                                    orient="auto-start-reverse"
                                    markerUnits="strokeWidth"
                                >
                                    <path d="M 0 0 L 10 5 L 0 10 z" fill="context-stroke" />
                                </marker>
                            </defs>

                            {renderGrid()}

                            {renderShownLines(question.lines)}
                            {renderShownPoints(question.points)}

                            {renderStudentLines()}
                            {renderStudentPoints()}

                            {renderPointPreview()}
                            {renderLinePreview()}
                        </svg>

                        {pointPopup}
                        {linePopup}
                    </Box>
                </Box>
            </Paper>

            {showScoring && <GraphingScoring question={question} userResponse={userResponse} />}
            <ErrorMessage error={error} />
        </Stack>
    );
}

function snapTo(val: number, step: number) {
    const st = Number(step);
    if (!isFinite(st) || st <= 0) return val;
    const k = Math.round(val / st);
    return k * st;
}
// Select values that land on a given stride, anchored to a base (0 keeps symmetry)
function onStride(value: number, step: number, stride: number, base = 0, eps = 1e-9) {
    // period = how many world units between drawn ticks
    const period = step * Math.max(1, stride | 0);
    if (period <= eps) return false;
    const k = Math.round((value - base) / period);
    return Math.abs(base + k * period - value) <= eps;
}

function buildGridTicks(
    minX: number,
    maxX: number,
    minY: number,
    maxY: number,
    stepX: number,
    stepY: number,
    minorPerMajor = 2
) {
    // Coerce to numbers to avoid string concatenation + toFixed() errors
    let sx = Number(stepX);
    let sy = Number(stepY);
    sx = isFinite(sx) && sx > 0 ? sx : 1;
    sy = isFinite(sy) && sy > 0 ? sy : 1;

    minorPerMajor = minorPerMajor >= 1 ? Math.floor(minorPerMajor) : 2;

    const xsMajor: number[] = [];
    const ysMajor: number[] = [];
    const xsMinor: number[] = [];
    const ysMinor: number[] = [];

    const startX = Math.ceil(minX / sx) * sx;
    for (let x = startX; x <= maxX + 1e-9; x = Number((x + sx).toFixed(10))) {
        xsMajor.push(x);
    }
    const startY = Math.ceil(minY / sy) * sy;
    for (let y = startY; y <= maxY + 1e-9; y = Number((y + sy).toFixed(10))) {
        ysMajor.push(y);
    }

    const mx = sx / minorPerMajor;
    const my = sy / minorPerMajor;

    const minorStartX = Math.ceil(minX / mx) * mx;
    for (let x = minorStartX; x <= maxX + 1e-9; x = Number((x + mx).toFixed(10))) {
        if (Math.abs(x / sx - Math.round(x / sx)) > 1e-9) xsMinor.push(x);
    }
    const minorStartY = Math.ceil(minY / my) * my;
    for (let y = minorStartY; y <= maxY + 1e-9; y = Number((y + my).toFixed(10))) {
        if (Math.abs(y / sy - Math.round(y / sy)) > 1e-9) ysMinor.push(y);
    }

    return { xsMajor, ysMajor, xsMinor, ysMinor, stepX: sx, stepY: sy };
}

// Small helpers for labels
function isApproximatelyZero(v: number) {
    return Math.abs(v) < 1e-9;
}
function formatTick(v: number) {
    const n = isApproximatelyZero(v) ? 0 : v;
    // avoid long floats like 1.0000000001
    const s = Math.abs(n) >= 1 ? n.toFixed(3) : n.toPrecision(6);
    return parseFloat(s).toString();
}

function clamp(n: number, lo: number, hi: number) {
    return Math.max(lo, Math.min(hi, n));
}

function extendEndpoints(
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    style: 'segment' | 'ray' | 'line',
    minX: number,
    maxX: number,
    minY: number,
    maxY: number,
    stepX: number,
    stepY: number,
    blocks = 0.25
) {
    const dx = x2 - x1,
        dy = y2 - y1;
    const len = Math.hypot(dx, dy) || 1;
    const ux = dx / len,
        uy = dy / len;

    const sx = Number(stepX);
    const sy = Number(stepY);
    const grid = Math.max(
        1e-9,
        Math.min(isFinite(sx) && sx > 0 ? sx : 1, isFinite(sy) && sy > 0 ? sy : 1)
    );
    const ext = blocks * grid;

    let ax = x1,
        ay = y1;
    let bx = x2,
        by = y2;

    if (style === 'segment') {
        // no extensions
    } else if (style === 'ray') {
        // extend only from the second point
        bx = x2 + ux * ext;
        by = y2 + uy * ext;
    } else {
        // extend from both endpoints
        ax = x1 - ux * ext;
        ay = y1 - uy * ext;
        bx = x2 + ux * ext;
        by = y2 + uy * ext;
    }

    ax = clamp(ax, minX, maxX);
    ay = clamp(ay, minY, maxY);
    bx = clamp(bx, minX, maxX);
    by = clamp(by, minY, maxY);
    return { ax, ay, bx, by };
}

// Handle temporarily undefined or NaN y values
function coerceNum(v: unknown): number | null {
    if (typeof v === 'number') return Number.isFinite(v) ? v : null;
    const n = parseFloat(String(v));
    return Number.isFinite(n) ? n : null;
}

// SCORING //
interface GraphingScoringProps {
    question: QtiGraphingQuestionDTO;
    userResponse: string;
}

function GraphingScoring({ question, userResponse }: GraphingScoringProps) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [isCorrect, setIsCorrect] = useState(false);
    const [answerChecked, setAnswerChecked] = useState(false);
    const [message, setMessage] = useState('');

    useEffect(() => {
        setAnswerChecked(false);
        setMessage('');
    }, [userResponse]);

    const handleCheckAnswer = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const { isCorrect: isDeterminedCorrect, message } = await submitForGrading(
                question,
                userResponse
            );
            setIsCorrect(isDeterminedCorrect);
            setMessage(message ?? '');
            setAnswerChecked(true);
        } catch {
            setError('Failed to check answer.');
            setIsCorrect(false);
        } finally {
            setLoading(false);
        }
    }, [question, userResponse]);

    if (question.meta.isHandScored === true) return null;

    const parsedResponse = userResponse ? JSON.parse(userResponse) : null;
    if (!parsedResponse) return null;

    const studentPoints = parsedResponse?.points || [];
    const studentLines = parsedResponse?.lines || [];
    const hasAnswer = studentPoints.length > 0 || studentLines.length > 0;

    return (
        <div className="mt-4 flex items-center space-x-4">
            <div>
                <SimpleButton
                    color="success"
                    onClick={handleCheckAnswer}
                    disabled={!hasAnswer || answerChecked || loading}
                    variant="contained"
                    className="px-4 py-1 rounded disabled:opacity-50"
                >
                    Check Answer
                </SimpleButton>
                {loading && <Loading message="Checking answer..." />}
            </div>

            {error ? (
                <ErrorMessage error={error} />
            ) : (
                <span
                    className={`${isCorrect ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}`}
                >
                    {answerChecked && hasAnswer ? message : '\u00A0'}
                </span>
            )}
        </div>
    );
}
